import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_REPORTE_INGRESOS, GET_REPORTE_ADR_REVPAR, GET_REPORTE_OCUPACION } from '../../graphql/reportes';
import { GET_RESUMEN_EGRESOS } from '../../graphql/gastos';
import { DollarSign, TrendingUp, Percent, TrendingDown, Receipt, ShoppingCart, Clock } from 'lucide-react';
import './EstadisticasRecientes.css';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
  }).format(value || 0);

function EstadisticasRecientes() {
  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  const hace30 = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
  })();

  const [fechaDesde, setFechaDesde] = useState(hace30);
  const [fechaHasta, setFechaHasta] = useState(hoy);

  const { data: ingresosData, loading: loadingIngresos } = useQuery(GET_REPORTE_INGRESOS, {
    variables: { fechaDesde, fechaHasta },
    fetchPolicy: 'cache-and-network',
  });

  const { data: kpiData, loading: loadingKpi } = useQuery(GET_REPORTE_ADR_REVPAR, {
    variables: { fechaDesde, fechaHasta },
    fetchPolicy: 'cache-and-network',
  });

  const { data: ocupData, loading: loadingOcup } = useQuery(GET_REPORTE_OCUPACION, {
    variables: { fechaDesde, fechaHasta },
    fetchPolicy: 'cache-and-network',
  });

  const { data: egresosData, loading: loadingEgresos } = useQuery(GET_RESUMEN_EGRESOS, {
    variables: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
    fetchPolicy: 'cache-and-network',
  });

  const ingresos = ingresosData?.reporteIngresos;
  const kpi = kpiData?.reporteADRRevPAR?.resumen;
  const ocupacion = ocupData?.reporteOcupacion;
  const egresos = egresosData?.resumenEgresos;
  const loading = loadingIngresos || loadingKpi || loadingOcup || loadingEgresos;

  const balanceNeto = (ingresos?.total_ingresos || 0) - (egresos?.total_egresos || 0);
  const maxCatTotal = egresos?.gastos_por_categoria?.length > 0
    ? Math.max(...egresos.gastos_por_categoria.map(c => c.total))
    : 1;

  return (
    <div className="er">
      <div className="er__header">
        <h2 className="er__title">Estadísticas recientes</h2>
        <p className="er__subtitle">Revisa las estadísticas de tu alojamiento en las últimas semanas</p>
      </div>

      <div className="er__fecha-row">
        <span className="er__fecha-label">Mostrando estadísticas para:</span>
        <input type="date" className="er__fecha-input" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        <span>-</span>
        <input type="date" className="er__fecha-input" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
      </div>

      {loading ? (
        <div className="er__loading">Cargando estadísticas...</div>
      ) : (
        <>
          {/* Income cards */}
          <div className="er__cards">
            <div className="er__card er__card--ingresos">
              <span className="er__card-label">Ingresos totales</span>
              <span className="er__card-value">{formatCurrency(ingresos?.total_ingresos)}</span>
              <div className="er__card-breakdown">
                <div className="er__bar-row">
                  <span className="er__bar-label">Alojamiento</span>
                  <div className="er__bar">
                    <div
                      className="er__bar-fill er__bar-fill--alojamiento"
                      style={{ width: `${ingresos?.porcentaje_hospedajes || 0}%` }}
                    />
                  </div>
                  <span className="er__bar-value">{formatCurrency(ingresos?.ingresos_hospedajes)}</span>
                </div>
                <div className="er__bar-row">
                  <span className="er__bar-label">Ingresos extras</span>
                  <div className="er__bar">
                    <div
                      className="er__bar-fill er__bar-fill--extras"
                      style={{ width: `${ingresos?.porcentaje_consumos || 0}%` }}
                    />
                  </div>
                  <span className="er__bar-value">{formatCurrency(ingresos?.ingresos_consumos)}</span>
                </div>
              </div>
            </div>

            <div className="er__card er__card--balance">
              <span className="er__card-label">Balance neto</span>
              <span className={`er__card-value ${balanceNeto >= 0 ? 'er__card-value--balance' : 'er__card-value--negativo'}`}>
                {formatCurrency(balanceNeto)}
              </span>
              <span className="er__card-detail">{ingresos?.num_facturas || 0} facturas emitidas</span>
            </div>
          </div>

          {/* Egresos cards */}
          <div className="er__cards">
            <div className="er__card er__card--egresos">
              <span className="er__card-label">Total egresos</span>
              <span className="er__card-value er__card-value--egresos">{formatCurrency(egresos?.total_egresos)}</span>
              <div className="er__card-breakdown">
                <div className="er__bar-row">
                  <span className="er__bar-label"><Receipt size={12} /> Gastos</span>
                  <div className="er__bar">
                    <div
                      className="er__bar-fill er__bar-fill--gastos"
                      style={{ width: `${egresos?.total_egresos ? ((egresos?.total_gastos || 0) / egresos.total_egresos * 100) : 0}%` }}
                    />
                  </div>
                  <span className="er__bar-value">{formatCurrency(egresos?.total_gastos)}</span>
                </div>
                <div className="er__bar-row">
                  <span className="er__bar-label"><ShoppingCart size={12} /> Compras</span>
                  <div className="er__bar">
                    <div
                      className="er__bar-fill er__bar-fill--compras"
                      style={{ width: `${egresos?.total_egresos ? ((egresos?.total_compras || 0) / egresos.total_egresos * 100) : 0}%` }}
                    />
                  </div>
                  <span className="er__bar-value">{formatCurrency(egresos?.total_compras)}</span>
                </div>
              </div>
            </div>

            <div className="er__card er__card--pendientes">
              <span className="er__card-label"><Clock size={14} /> Pendientes de pago</span>
              <span className="er__card-value er__card-value--pendientes">{formatCurrency(egresos?.gastos_pendientes_pago)}</span>
              <span className="er__card-detail">Gastos sin pagar en el periodo</span>
            </div>
          </div>

          {/* Gastos por categoria */}
          {egresos?.gastos_por_categoria?.length > 0 && (
            <div className="er__card er__card--categorias">
              <span className="er__card-label">Gastos por categoria</span>
              <div className="er__categorias-list">
                {egresos.gastos_por_categoria.map((cat, i) => (
                  <div key={i} className="er__cat-row">
                    <span className="er__cat-dot" style={{ background: cat.color || '#6b7280' }} />
                    <span className="er__cat-name">{cat.categoria_nombre}</span>
                    <div className="er__cat-bar">
                      <div
                        className="er__cat-bar-fill"
                        style={{ width: `${(cat.total / maxCatTotal) * 100}%`, background: cat.color || '#6b7280' }}
                      />
                    </div>
                    <span className="er__cat-value">{formatCurrency(cat.total)}</span>
                    <span className="er__cat-pct">{cat.porcentaje?.toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* KPI cards */}
          {kpi && (
            <div className="er__kpis">
              <div className="er__kpi">
                <DollarSign size={20} className="er__kpi-icon" />
                <div>
                  <span className="er__kpi-label">ADR</span>
                  <span className="er__kpi-value">{formatCurrency(kpi.adr_global)}</span>
                </div>
              </div>
              <div className="er__kpi">
                <TrendingUp size={20} className="er__kpi-icon" />
                <div>
                  <span className="er__kpi-label">RevPAR</span>
                  <span className="er__kpi-value">{formatCurrency(kpi.rev_par_global)}</span>
                </div>
              </div>
              <div className="er__kpi">
                <TrendingUp size={20} className="er__kpi-icon" />
                <div>
                  <span className="er__kpi-label">TRevPAR</span>
                  <span className="er__kpi-value">{formatCurrency(kpi.t_rev_par_global)}</span>
                </div>
              </div>
              <div className="er__kpi">
                <Percent size={20} className="er__kpi-icon" />
                <div>
                  <span className="er__kpi-label">Ocupación</span>
                  <span className="er__kpi-value">{(kpi.occupancy_rate_global || 0).toFixed(1)}%</span>
                  <span className="er__kpi-sub">{kpi.total_noches_vendidas || 0} noches vendidas</span>
                </div>
              </div>
            </div>
          )}

          {/* Occupancy chart */}
          {ocupacion?.ocupacion_por_dia?.length > 0 && (
            <div className="er__chart-section">
              <h3 className="er__chart-title">Ocupación promedio</h3>
              <div className="er__chart-value">{(ocupacion.porcentaje_ocupacion_promedio || 0).toFixed(2)}%</div>
              <div className="er__chart">
                {ocupacion.ocupacion_por_dia.slice(-14).map((dia, i) => (
                  <div key={i} className="er__chart-bar-wrapper" title={`${dia.fecha}: ${dia.porcentaje_ocupacion?.toFixed(1)}%`}>
                    <div
                      className="er__chart-bar"
                      style={{ height: `${Math.max(dia.porcentaje_ocupacion || 0, 2)}%` }}
                    />
                    <span className="er__chart-label">
                      {(() => {
                        if (!dia.fecha) return '—';
                        const parts = dia.fecha.split('T')[0].split('-');
                        if (parts.length === 3) {
                          const d = new Date(parts[0], parts[1] - 1, parts[2]);
                          return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit' });
                        }
                        return '—';
                      })()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EstadisticasRecientes;
